import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate, useParams } from "react-router-dom";
import {
  ArrowBack
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  Campaign, 
  CampaignCharOptions, 
  COptions,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import LoadingBox from "@components/input/LoadingBox";

import CharacterOptionsInput from "@dndComponents/character/misc/CharacterOptionsInput";
import TabMenu from '@components/input/menus/TabMenu';
import CampaignCharactersInput from '@dndComponents/campaign/characters/CampaignCharactersInput';
import CampaignPlayersInput from '@dndComponents/campaign/players/CampaignPlayersInput';
import CampaignNotesInput from '@dndComponents/campaign/notes/CampaignNotesInput';
import CampaignEncountersInput from '@dndComponents/campaign/CampaignEncountersInput';
import CampaignNPCsInput from '@dndComponents/campaign/npcs/CampaignNPCsInput';
import QuickRulesControl from '@dndComponents/campaign/QuickRulesControl';
import FlexBox from '@components/layout/FlexBox';
import { useGetWidthCategory } from '@utilities/common_hooks';
import FloatMenu from '@components/input/menus/FloatMenu';
import CampaignQuestBoardsInput from '@dndComponents/campaign/quest_boards/CampaignQuestBoardsInput';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import { CALENDAR_OPTIONS, CALENDARS, CalendarSystem, findHolidaysForMonth, findSeasonsForMonth, getMMDDFromYearDay, mmddRangeString, mmddString, parseDate } from '@dndModels/Calendars';
import CalendarBox from '@components/input/calendar/CalendarBox';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignDetails() {
  const { id } = useParams();
  const { ref, widthCategory } = useGetWidthCategory();
  const [loading, setLoading] = useState(false);
  const [logging_in, setLoggingIn] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<Campaign>(new Campaign());
  const [tab, setTab] = useState("Characters");
  const [ping, setPing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const check_login = useCallback( async () => {
    setTimeout(() => {
      if (api.user === null) {
        check_login();
      } else {
        setLoggingIn(false);
        load();
      }
    }, 1000);
  }, []);

  const loadObject = useCallback(async (campaign_id: string) => {
    // debugger;
    const campaign = await api.getCampaign({ campaign_id, include: ["players", "characters"] });
    if (campaign) {
      if (api.user && api.user._id === campaign.owner_id) {
        setTab("Details");
      }
      setObj(campaign);
    }
    setLoading(false);
  }, []);

  const load = useCallback(() => {
    console.log("load()");
    if (id && id !== "" && obj._id !== id) {
      loadObject(id);
    } else {
      setLoading(false);
    }
    setLoggingIn(false);
  }, [id, obj, loadObject]);
  
  useEffect(() => {
    if(!fetched) {
      setFetched(true);
      setLoading(true);
      if (api.user === null) {
        const userObjStr = localStorage.getItem("loginUser");
        if (!userObjStr) {
          setNavigateTo("/");
        } else {
          setLoggingIn(true);
          check_login();
        }
      } else {
        load();
      }
    }
  }, [fetched, load, check_login]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const saveCampaign = useCallback( async (campaign: Campaign) => {
    setProcessing(true);
    const last_updated = common_util.get_time_number();
    campaign.last_updated = last_updated;
    if (campaign._id && campaign._id !== "") {
      await api.updateObject("campaign", campaign);
    } else {
      campaign.owner_id = api.user ? api.user._id : "No Login";
      await api.createObject("campaign", campaign);
    }
    setProcessing(false);
  }, []);

  const campaignChanged = useCallback(() => { 
    console.log("Campaign Save Tools - Changed");
    setTimeout(() => {
      saveCampaign(obj);
    }, 100);
  }, [obj]);

  useEffect(() => {
    console.log("Campaign Save Tools");
    obj.subscription_holder.subscribe("Campaign", mounted, campaignChanged);
  }, [obj, campaignChanged]);

  const renderMonthDetails = () => {
    const calendar = CALENDARS.find(o => o.true_id === obj.calendar_id);
    const parsed_today = parseDate(obj.current_date);
    const month = parsed_today.month;
    const current_month = calendar ? calendar.months.find(o => o.position === month) : undefined;
    if (calendar && current_month) {
      // debugger;
      const seasons = findSeasonsForMonth(calendar, month);
      const holidays = findHolidaysForMonth(calendar, month);
      
      return (
        <FlexBox direction="column">
          <FlexBox xs_expand={0} className="header centered padded">{ current_month.name }</FlexBox>
          <FlexBox xs_expand={0}>Season{ seasons.length > 1 ? "s" : "" }: { seasons.map(o => `${o.name} (${mmddRangeString({ mmdd_start: o.mmdd_start, mmdd_end: o.mmdd_end, calendar })})`).join(", ") }</FlexBox>
          { holidays.length > 0 &&
            <FlexBox xs_expand={0}>Holiday{ holidays.length > 1 ? "s" : "" }: { holidays.map(o => {
              const use_mmdd = o.mmdd ? o.mmdd : (o.year_day ? getMMDDFromYearDay({ calendar, year_day: o.year_day }) : -1);
              return `${o.name} (${mmddRangeString({ mmdd_start: use_mmdd, length: o.length, calendar })})`;
            }).join(", ") }</FlexBox>
          }
        </FlexBox>
      );
    }
    return null;
  }

  const renderTab = () => {
    const user = api.user;
    if (user) {
      if (tab === "Details") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid size={{ lg: 6, xs: 12 }}>
              <Grid container spacing={0} direction="row">
                <Grid size={6}>
                  <StringBox 
                    value={obj.name} 
                    message={obj.name.length > 0 ? "" : "Name Invalid"} 
                    name="Name"
                    onBlur={(changed: string) => {
                      const new_obj = obj;
                      new_obj.name = changed;
                      setObj(new_obj);
                      new_obj.changed();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid size={6}>
                  <StringBox 
                    name="Team Name"
                    value={obj.party_name} 
                    onBlur={(changed: string) => {
                      const new_obj = obj;
                      new_obj.party_name = changed;
                      setObj(new_obj);
                      new_obj.changed();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                { obj.calendar_id !== "" &&
                  <Grid size={12}>
                    <StringBox 
                      value={obj.description} 
                      name="Description"
                      multiline
                      onChange={(changed: string) => {
                        const new_obj = obj;
                        new_obj.description = changed;
                        setObj(new_obj);
                        new_obj.changed();
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                }
              </Grid>
            </Grid>
            <Grid size={{ lg: 6, xs: 12 }}>
              <Grid container spacing={0} direction="row">
                <Grid size={12}>
                  <SelectStringBox 
                    name="Calendar"
                    value={obj.calendar_id} 
                    options={CALENDAR_OPTIONS}
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      new_obj.calendar_id = changed;
                      setObj(new_obj);
                      new_obj.changed();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                { obj.calendar_id !== "" &&
                  <>
                    <Grid size={{ md: 6, xs: 12 }} className="padded centered">
                      <CalendarBox 
                        name="Current Date"
                        calendar_id={obj.calendar_id}
                        value={obj.current_date} 
                        onChange={(changed) => {
                          const new_obj = obj;
                          new_obj.current_date = changed;
                          setObj(new_obj);
                          new_obj.changed();
                          setPing(!ping);
                        }}
                      />
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }} className="padded">
                      { renderMonthDetails() }
                    </Grid>
                  </>
                }
              </Grid>
            </Grid>
            { obj.calendar_id === "" &&
              <Grid size={12}>
                <StringBox 
                  value={obj.description} 
                  name="Description"
                  multiline
                  onChange={(changed: string) => {
                    const new_obj = obj;
                    new_obj.description = changed;
                    setObj(new_obj);
                    new_obj.changed();
                    setPing(!ping);
                  }}
                />
              </Grid>
            }
          </Grid>
        ); 
      } else if (tab === "Characters") {
        return (
          <CampaignCharactersInput
            campaign={obj}
          />
        );
      } else if (tab === "Players") {
        return (
          <CampaignPlayersInput
            campaign={obj}
          />
        ); 
      } else if (tab === "Options") {
        return (
          <CharacterOptionsInput 
            obj={obj}
            disabled={processing || !api.user || api.user._id !== obj.owner_id}
            onChange={ async (options: COptions) => {
              // debugger;
              const new_obj = obj;
              let c_char_options = new_obj.char_options;
              if (!c_char_options) {
                c_char_options = new CampaignCharOptions();
                c_char_options.campaign_id = new_obj._id;
                c_char_options.campaign = new_obj;
              } 
              c_char_options.char_options = options.clone();
              await api.upsertObject("campaign_char_options", c_char_options);
              new_obj.char_options = c_char_options;
              new_obj.last_updated = common_util.get_time_number();
              await api.upsertObject("campaign", new_obj);
              setObj(new_obj);
            }}
          />
        ); 
      } else if (tab === "NPCs") {
        return (
          <CampaignNPCsInput
            campaign={obj}
          />
        ); 
      } else if (tab === "Notes") {
        return (
          <CampaignNotesInput
            campaign={obj}
          />
        ); 
      } else if (tab === "Encounters") {
        return (
          <CampaignEncountersInput
            campaign={obj}
          />
        );
      } else if (tab === "Rules") {
        return (
          <QuickRulesControl
            campaign={obj}
          />
        ); 
      } else if (tab === "Quest Boards") {
        return (
          <CampaignQuestBoardsInput
            campaign={obj}
          />
        ); 
      }
    } 
    return (<>Coming Soon</>);
  }

  const renderMenu = () => {
    const menu = api.user && api.user._id === obj.owner_id ? ["Details","Characters","Players","NPCs","Notes","Encounters","Quest Boards","Options","Rules"] : ["Characters","Players","Quest Boards","Options","Rules"];
    // if (obj.character_options.use_domt) {
    //   menu.push("Deck of Many Things");
    // }
    if (widthCategory === "lg") {
      return (
        <TabMenu selected={tab} onSelect={setTab} options={menu} />
      );
    } else {
      return (
        <FloatMenu selected={tab} onSelect={setTab} options={menu} />
      );
    }
  }
  
  const height = window.innerHeight - 64;
  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (!api.online) {
    return (
      <>Campaign Editing only works while online.  Try reconnecting.</>
    );
  } else if (!fetched || loading || logging_in) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else { 
    return (
      <FlexBox direction="column">
        <FlexBox direction="row">
          <FlexBox xs_expand={0} className="padded">
            <Tooltip title={`Back to Campaigns`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  setNavigateTo(`/campaign`);
                }}>
                <ArrowBack/>
              </Fab>
            </Tooltip> 
          </FlexBox>
          <FlexBox xs_expand={0} className="header centered" style={{ width: "200px" }}>
            { obj._id === "" ? "Create Campaign" : `Edit ${obj.name}` }
          </FlexBox>
          <FlexBox>
            { renderMenu() }
          </FlexBox>
        </FlexBox>
        <FlexBox>
          { renderTab() }
        </FlexBox>
      </FlexBox>
    ); 
  }
}
